import styles from "./EntrepreneurAccountData.module.css";

import newImageIcon from "../../assets/newImageIcon.svg";
import plusIcon2 from "../../assets/plusIcon2.svg";

import InputMask from "react-input-mask";

import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";

import BusinessHour from "../BusinessHour";
import api from "../../utils/axios/api";

import * as Types from "./types";
import { EntrepreneurDataSchema } from "./schemas";
import type { ZodError } from "zod";
import axios from "axios";

import ModalEncerrarLoja from "../ModalEncerrarLoja/ModalEncerraLoja";
import ModalAceitarTermos from "../ModalAceitarTermos/ModalAceitarTermos";

const FALLBACK_STORE_IMAGE =
  "https://img.freepik.com/vetores-gratis/design-de-logotipo-local-da-loja-gradiente_23-2149613160.jpg?semt=ais_hybrid";

function openingHoursForApi(oh: Types.OpeningHoursType) {
  function dayHours(day: Types.OpeningHoursValues) {
    return {
      abertura: day.active ? (day.abertura ?? "") : "",
      fechamento: day.active ? (day.fechamento ?? "") : "",
    };
  }

  return {
    segundaFeira: dayHours(oh.segundaFeira),
    tercaFeira: dayHours(oh.tercaFeira),
    quartaFeira: dayHours(oh.quartaFeira),
    quintaFeira: dayHours(oh.quintaFeira),
    sextaFeira: dayHours(oh.sextaFeira),
    sabado: dayHours(oh.sabado),
    domingo: {
      abertura: oh.domingo.active ? (oh.domingo.abertura ?? null) : null,
      fechamento: oh.domingo.active ? (oh.domingo.fechamento ?? null) : null,
    },
  };
}

function formatEntrepreneurSchemaErrors(
  error: ZodError,
  translate: (key: string) => string
): string {
  return error.issues
    .map((issue) => {
      let msg =
        typeof issue.message === "string"
          ? issue.message
          : String(issue.message ?? "");
      if (msg.startsWith("accountMenu.")) {
        const tr = translate(msg);
        if (tr && tr.trim() !== "" && tr !== msg) {
          msg = tr;
        }
      }
      return `${msg}`;
    })
    .join("\n");
}

function appendEntrepreneurUserFormData(
  fd: FormData,
  user: Record<string, unknown>,
  bank: { account: string; agency: string; pix: string }
) {
  fd.append("name", String(user.name ?? ""));
  fd.append("email", String(user.email ?? ""));
  fd.append("isUserDeleted", String(!!user.isUserDeleted));
  fd.append("confirmedEmail", String(!!user.confirmedEmail));
  fd.append("document", String(user.document ?? ""));
  fd.append("documentType", String(user.documentType ?? ""));
  fd.append("role", String(user.role ?? ""));
  fd.append("telephone", String(user.telephone ?? ""));
  const prevUrl =
    typeof user.image === "string" && user.image.startsWith("http")
      ? user.image
      : "";
  fd.append("image", prevUrl);
  fd.append("account", bank.account);
  fd.append("agency", bank.agency);
  fd.append("pix", bank.pix);
}

type States = State[];
type Cities = City[];

export interface State {
  id: number;
  nome: string;
  regiao: {
    id: number;
    nome: string;
    sigla: string;
  };
  sigla: string;
}

export interface City {
  id: number;
  nome: string;
}

export interface PasswordsType {
  password: string;
  repeatedPassword: string;
}

const EntrepreneurAccountData = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Types.EntrepreneurData>({
    entrepreneurData: {
      image: "",
      account: "",
      agency: "",
      pix: "",
    },
    stores: [
      {
        name: "",
        serviceRadius: 1,
        serviceDelivery: false,
        servicePickup: false,
        address: {
          state: "Uf",
          city: `${t(`accountMenu.select`)}`,
          street: "",
          district: "",
          complement: "",
          zipCode: "",
          number: "",
        },
        openingHours: {
          padrao: { abertura: "", fechamento: "", active: false },
          segundaFeira: {
            abertura: "",
            fechamento: "",
            active: false,
          },
          tercaFeira: { abertura: "", fechamento: "", active: false },
          quartaFeira: {
            abertura: "",
            fechamento: "",
            active: false,
          },
          quintaFeira: {
            abertura: "",
            fechamento: "",
            active: false,
          },
          sextaFeira: { abertura: "", fechamento: "", active: false },
          sabado: { abertura: "", fechamento: "", active: false },
          domingo: { abertura: "", fechamento: "", active: false },
        },
      },
    ],
  });
  const userId = localStorage.getItem("userId");

  //TODO: O usuário já deve ser acessível nesta página
  const [userBackend, setUserBackend] =
    useState<Types.EntrepreneurBackendData | null>(null);
  const [storeBackend, setStoreBackend] =
    useState<Types.StoreBackendData | null>(null);

  const [states, setStates] = useState<States>([]);
  const [cities, setCities] = useState<Cities>([]);
  const [showFinishStoreModal, setShowFinishStoreModal] = useState(false);
  const [passwords, setPasswords] = useState<PasswordsType>({
    password: "",
    repeatedPassword: "",
  });
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [entrepreneurLogoFile, setEntrepreneurLogoFile] =
    useState<File | null>(null);

  useEffect(() => {
    async function fetchStates() {
      const statesAPI = await fetch(
        "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
      );
      const statesData = await statesAPI.json();
      setStates(statesData);
    }
    fetchStates();
  }, []);

  useEffect(() => {
    if (
      formData.stores[0].address.state &&
      formData.stores[0].address.state !== "Uf"
    ) {
      fetchCities(formData.stores[0].address.state);
    }
  }, [states, formData.stores[0].address.state]);

  // Função para buscar municípios dado o ID do estado
  async function fetchCities(stateSigla: string) {
    const response = await fetch(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateSigla}/municipios`
    );
    const data: Cities = await response.json();
    setCities(data);
  }

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();

    const fetchData = async () => {
      try {
        const response = await api.get(`/user/${userId}`);

        setUserBackend(response.data);
        if (
          response.data.entrepeneur &&
          response.data.entrepeneur.store.length > 0
        ) {
          const store = response.data.entrepeneur.store[0];
          const address = response.data.entrepeneur.store[0].address;
          setStoreBackend(store);
          setIsAllBrazilDelivery(store.isAllBrazil)

          const openingHours: any = {};

          if (store.openingHours) {
            for (const key in store.openingHours) {
              openingHours[key] = {
                abertura: store.openingHours[key].abertura ?? "",
                fechamento: store.openingHours[key].fechamento ?? "",
                active:
                  store.openingHours[key].abertura &&
                  store.openingHours[key].fechamento
                    ? true
                    : false,
              };
            }
          }

          const mappedFormData = {
            entrepreneurData: {
              image: response.data.image || "",
              account: response.data.entrepeneur.account || "",
              agency: response.data.entrepeneur.agency || "",
              pix: response.data.entrepeneur.pix || "",
            },
            stores: [
              {
                name: store.name || "",
                serviceRadius: store.serviceRadius || 1,
                serviceDelivery: store.serviceDelivery || false,
                servicePickup: store.servicePickup || false,
                address: {
                  state: address.state || "Uf",
                  city: address.city || "",
                  street: address.street || "",
                  district: address.district || "",
                  complement: address.complement || "",
                  zipCode: address.zipCode || "",
                  number: address.number ?? "",
                  name: address.name || "",
                },
                openingHours:
                  Object.keys(openingHours).length > 0
                    ? openingHours
                    : mapOpeningHours(store.openingHours),
              },
            ],
          };

          setFormData(mappedFormData);
        }
      } catch (error: any) {
        console.error(error);
      }
    };

    fetchData();

    return () => {
      cancelTokenSource.cancel(
        "Componente desmontado: a requisição foi cancelada."
      );
    };
  }, [userId]);

  // Função auxiliar para mapear e ajustar os horários de abertura
  function mapOpeningHours(
    openingHours: Types.OpeningHoursType
  ): Types.OpeningHoursType {
    // Define valores padrão para cada dia, incluindo 'padrao'
    const defaultValues: Types.OpeningHoursValues = {
      abertura: "",
      fechamento: "",
      active: false,
    };

    let completeOpeningHoursWithPadrao: Types.OpeningHoursType = {
      padrao: { ...defaultValues }, // Inicializa 'padrao' com valores padrão
      segundaFeira: { ...defaultValues },
      tercaFeira: { ...defaultValues },
      quartaFeira: { ...defaultValues },
      quintaFeira: { ...defaultValues },
      sextaFeira: { ...defaultValues },
      sabado: { ...defaultValues },
      domingo: { ...defaultValues },
    };

    // Atualiza os valores com base nos dados recebidos e aplica formatação
    const storeOpeningHoursStatus:
      | { [key in keyof Types.OpeningHoursType]?: boolean }
      | undefined = JSON.parse(
      localStorage.getItem("storeOpeningHoursStatus") as string
    );
    Object.keys(openingHours).forEach((day) => {
      const dayKey = day as keyof Types.OpeningHoursType;
      if (openingHours[dayKey]) {
        completeOpeningHoursWithPadrao[dayKey] = {
          abertura: openingHours[dayKey].abertura
            ? formatBusinessTime(openingHours[dayKey].abertura)
            : "",
          fechamento: openingHours[dayKey].fechamento
            ? formatBusinessTime(openingHours[dayKey].fechamento)
            : "",
          active: storeOpeningHoursStatus
            ? (storeOpeningHoursStatus[dayKey] ?? false)
            : false,
        };
      }
    });

    const standardOpeningHours:
      | { abertura: string; fechamento: string }
      | undefined = JSON.parse(
      localStorage.getItem("standardOpeningHours") as string
    );
    // Garante que 'padrao', se não fornecido, seja formatado corretamente se vier a ser definido depois
    completeOpeningHoursWithPadrao["padrao"] = standardOpeningHours
      ? {
          ...standardOpeningHours,
          active: storeOpeningHoursStatus
            ? (storeOpeningHoursStatus["padrao"] ?? false)
            : false,
        }
      : { ...defaultValues };

    return completeOpeningHoursWithPadrao;
  }

  const handleBusinessHourChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    setFormData((prev) => {
      const propertyName = event.target.name as keyof Types.OpeningHoursType;
      let propertyValue: string | boolean;

      if (field === "active") {
        propertyValue = event.target.checked;
      } else if (field === "from" || field === "to") {
        // Armazena o valor diretamente sem formatação no onChange
        propertyValue = event.target.value || "";
      } else {
        return prev;
      }

      // Atualiza o estado sem aplicar formatação
      return {
        ...prev,
        stores: prev.stores.map((store, index) => {
          if (index === 0) {
            const propertyToUpdate =
              field === "from"
                ? "abertura"
                : field === "to"
                ? "fechamento"
                : "active";
            return {
              ...store,
              openingHours: {
                ...store.openingHours,
                [propertyName]: {
                  ...store.openingHours[propertyName],
                  [propertyToUpdate]: propertyValue,
                },
              },
            };
          }
          return store;
        }),
      };
    });
  };

  const handleBusinessStandardHourChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    setFormData((prev) => {
      let propertyValue: string | boolean;

      if (field === "active") {
        propertyValue = event.target.checked;
      } else if (field === "from" || field === "to") {
        // Armazena o valor diretamente sem formatação no onChange
        propertyValue = event.target.value || "";
      } else {
        return prev;
      }

      // Atualiza o estado sem aplicar formatação
      return {
        ...prev,
        stores: prev.stores.map((store, index) => {
          if (index === 0) {
            const propertyToUpdate =
            field === "from"
            ? "abertura"
            : field === "to"
            ? "fechamento"
            : "active";
            const values = Object.keys(store.openingHours)

            const openingHours = values.reduce((acc, value) => {
              acc[value] = {
                ...store.openingHours[value],
                [propertyToUpdate]: propertyValue
              }

              return acc
            }, {})

            openingHours['padrao'] = {
              ...store.openingHours["padrao"],
                [propertyToUpdate]: propertyValue
            }

            return {
              ...store,
              openingHours: {
                ...store.openingHours,
                ...openingHours,
              },
            };
          }
          return store;
        }),
      };
    });
  };

  const formatBusinessTime = (value: string): string => {
    if (!value) return "";
    let [hours, minutes] = value.includes(":")
      ? value.split(":")
      : [value, "00"];
    hours = hours.padStart(2, "0");
    minutes = minutes.padEnd(2, "0").substring(0, 2); // Garante que minutos tenham 2 dígitos
    return `${hours}:${minutes}`;
  };

  const handleInputFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.currentTarget.files && event.currentTarget.files.length > 0) {
      const file = event.currentTarget.files[0];
      setEntrepreneurLogoFile(file);

      const readFile = (f: File) =>
        new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(f);
          reader.onload = () => {
            resolve(reader.result as string);
          };
        });

      const logoImageURL = await readFile(file);

      setFormData((prev) => ({
        ...prev,
        entrepreneurData: {
          ...prev.entrepreneurData,
          image: logoImageURL,
        },
      }));
    }
  };

  const handleCepBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const cep = event.target.value.replace(/\D/g, ""); // Remove caracteres não numéricos

    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();

        if (!data.erro) {
          setFormData((prev) => ({
            ...prev,
            stores: prev.stores.map((store, index) => {
              if (index === 0) {
                return {
                  ...store,
                  address: {
                    ...store.address,
                    state: data.uf,
                    city: data.localidade,
                  },
                };
              }
              return store;
            }),
          }));
        } else {
          console.error("CEP não encontrado.");
        }
      } catch (error) {
        console.error("Erro ao buscar dados do CEP:", error);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!storeBackend) {
      setTermsModalOpen(true);
    } else {
      await handleFinishStore();
    }
  };

  const handleFinishStore = async () => {
    if (!userBackend || !userId) {
      alert(t("accountMenu.userNotLoaded"));
      return;
    }

    const rawRadius = formData.stores[0].serviceRadius;
    const serviceRadiusResolved = isAllBrazilDelivery
      ? -1
      : rawRadius === -1
        ? 1
        : Math.max(1, rawRadius);

    const formPayload: Types.EntrepreneurData = {
      ...formData,
      stores: [
        {
          ...formData.stores[0],
          serviceRadius: serviceRadiusResolved,
        },
      ],
    };

    const result = EntrepreneurDataSchema.safeParse(formPayload);
    if (!result.success) {
      const text = formatEntrepreneurSchemaErrors(result.error, t);
      alert(text || JSON.stringify(result.error.issues));
      return;
    }

    const bank = {
      account: result.data.entrepreneurData.account,
      agency: result.data.entrepreneurData.agency,
      pix: result.data.entrepreneurData.pix,
    };
    const submitUserUpdate = () => {
      const fd = new FormData();
      appendEntrepreneurUserFormData(
        fd,
        userBackend as unknown as Record<string, unknown>,
        bank
      );
      if (entrepreneurLogoFile) {
        fd.append("file", entrepreneurLogoFile);
      }
      return api.put(`/user/${userBackend.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    };

    const storeImageUrl =
      storeBackend?.image ??
      (result.data.entrepreneurData.image?.startsWith("http")
        ? result.data.entrepreneurData.image
        : FALLBACK_STORE_IMAGE);

    const updatedUserStore = {
      ...(storeBackend ?? {}),
      ...result.data.stores[0],
      isAllBrazil: isAllBrazilDelivery,
      image: storeImageUrl,
      openingHours: openingHoursForApi(
        formPayload.stores[0].openingHours
      ),
    };

    const updatedAddress = {
      ...result.data.stores[0].address,
      name: "temporário",
    };

    try {
      let storeId = (updatedUserStore as { id?: string }).id ?? "";
      if (!storeId) {
        const [newUser, newAddress] = await Promise.all([
          submitUserUpdate(),
          api.post(`/address`, updatedAddress),
        ]);

        updatedUserStore.avaliation = 0;
        updatedUserStore.status = "aberto";
        updatedUserStore.category = "chaves";
        updatedUserStore.entrepeneurId = newUser.data.entrepeneur.id;
        updatedUserStore.addressId = newAddress.data.id;

        const response = await api.post(`/store`, updatedUserStore);
        storeId = response.data.id;
      } else {
        await Promise.all([
          submitUserUpdate(),
          api.put(`/address/${updatedUserStore.addressId}`, updatedAddress),
          api.put(`/store/${updatedUserStore.id}`, updatedUserStore),
        ]);
      }

      localStorage.setItem("storeId", storeId);
      setEntrepreneurLogoFile(null);

      if (isAllBrazilDelivery) {
        setFormData((prev) => ({
          ...prev,
          stores: prev.stores.map((s, i) =>
            i === 0 ? { ...s, serviceRadius: -1 } : s
          ),
        }));
      }

      alert(t(`accountMenu.dataUpdated`));

      window.location.reload();
      window.scrollTo(0, 0);
    } catch (error) {
      console.error(error);
      alert(t("accountMenu.saveError"));
    }
  };

  //TODO: Após exibir o modal e confirmar, excluir no banco de dados
  const handleFinishStoreSubmit: (password: string) => Promise<boolean> = async (
    password: string
  ) => {
    const storeId = localStorage.getItem("storeId");
    try {
      await api.delete(`/store/${storeId}`, {
        data: {
          userId,
          password,
        },
      }).then(() => {
        localStorage.removeItem("storeId")
        alert("Loja Encerrada!")
        window.location.assign("/")
      }).catch((err) => console.log(err))
  
      return true; // Sucesso na requisição
    } catch (error: any) {
      alert("Erro ao encerrar loja, verifique se a senha está correta!")
      
      return false; // Falha na requisição
    }
  };
  

  const [isAllBrazilDelivery, setIsAllBrazilDelivery] = useState(false);

  return (
    <div className={styles.main_container}>
      <h1>{t(`accountMenu.data`)}</h1>
      <form className={styles.main_form} onSubmit={handleSubmit}>
        <fieldset className={styles.fieldset_general_information}>
          <h2>{t(`accountMenu.generalInformation`)}</h2>
          <div className={styles.store_logo_container}>
            {/*O input em si fica escondido com display: none, a label que traz a funcionalidade*/}
            <input
              type="file"
              name="uploadInput"
              id="uploadInput"
              accept="image/*"
              onChange={(event) => handleInputFileChange(event)}
            />
            {formData.entrepreneurData.image === "" && (
              <label className={styles.label_default} htmlFor="uploadInput">
                <img className={styles.logo_image_default} src={newImageIcon} />
                <img className={styles.plus_icon} src={plusIcon2} />
              </label>
            )}

            {formData.entrepreneurData.image !== "" && (
              <label
                className={styles.label_personalized}
                htmlFor="uploadInput"
              >
                <img
                  className={styles.logo_image}
                  src={formData.entrepreneurData.image}
                />
                <img className={styles.plus_icon} src={plusIcon2} />
              </label>
            )}
          </div>
          <div className={styles.store_name_container}>
            <label htmlFor="storeName">{t(`accountMenu.name`)}:</label>
            <input
              type="text"
              required
              name="storeName"
              id="storeName"
              value={formData.stores[0].name}
              onChange={(event) =>
                setFormData((prev) => {
                  const updatedStores = [...prev.stores];

                  updatedStores[0] = {
                    ...updatedStores[0],
                    name: event.target.value,
                  };

                  return {
                    ...prev,
                    stores: updatedStores,
                  };
                })
              }
            />
          </div>
        </fieldset>

        <fieldset className={styles.fieldset_bank_data}>
          <h2>{t(`accountMenu.bankData`)}</h2>
          <h3>{t(`accountMenu.bankInfo`)}</h3>
          <div className={styles.bank_inputs_container}>
            <div className={styles.bank_input_container}>
              <label htmlFor="agencyInput">{t(`accountMenu.agency`)}</label>
              <InputMask
                className={styles.bank_input}
                mask={"9999"}
                id="agencyInput"
                name="agencyInput"
                value={formData.entrepreneurData.agency}
                onChange={(e) =>
                  setFormData((prev) => {
                    return {
                      ...prev,
                      entrepreneurData: {
                        ...prev.entrepreneurData,
                        agency: e.target.value.replace(/\D/g, ""),
                      },
                    };
                  })
                }
                required
              />
            </div>
            <div className={styles.bank_input_container}>
              <label htmlFor="accountInput">{t(`accountMenu.account`)}</label>
              <InputMask
                className={styles.bank_input}
                mask={"99999999-9"}
                id="accountInput"
                name="accountInput"
                value={formData.entrepreneurData.account}
                onChange={(e) =>
                  setFormData((prev) => {
                    return {
                      ...prev,
                      entrepreneurData: {
                        ...prev.entrepreneurData,
                        account: e.target.value.replace(/\D/g, ""),
                      },
                    };
                  })
                }
                required
              />
            </div>
            <div className={styles.bank_input_container}>
              <label htmlFor="pixInput">{t(`accountMenu.pix`)}</label>
              <input
                className={styles.bank_input}
                id="pixInput"
                type="text"
                name="pixInput"
                value={formData.entrepreneurData.pix}
                onChange={(e) =>
                  setFormData((prev) => {
                    return {
                      ...prev,
                      entrepreneurData: {
                        ...prev.entrepreneurData,
                        pix: e.target.value,
                      },
                    };
                  })
                }
                required
              />
            </div>
          </div>
        </fieldset>

        <fieldset className={styles.fieldset_business_hours}>
          <h2>{t(`accountMenu.businessHour`)}</h2>
          <h3>{t(`accountMenu.pickBusinessHour`)}</h3>
          <div className={styles.business_hours_container}>
            <BusinessHour
              onChangeActive={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleBusinessStandardHourChange(event, "active")
              }
              onChangeAbertura={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleBusinessStandardHourChange(event, "from")
              }
              onChangeFechamento={(
                event: React.ChangeEvent<HTMLInputElement>
              ) => handleBusinessStandardHourChange(event, "to")}
              businessHoursInfo={{
                name: "padrao",
                ...formData.stores[0].openingHours.padrao,
              }}
            />
            <div className={styles.working_days_titles}>
              <h4>{t(`accountMenu.workingDays`)}</h4>
              <h5>{t(`accountMenu.markWorkingDays`)}</h5>
            </div>
            <div className={styles.working_days_container}>
              <div className={styles.left_top_working_days}>
                {Object.keys(formData.stores[0].openingHours)
                  .slice(1, 5)
                  .map((key) => {
                    return (
                      <BusinessHour
                        key={key}
                        required={
                          formData.stores[0].openingHours[
                            key as keyof Types.OpeningHoursType
                          ].active
                        }
                        businessHoursInfo={{
                          name: key,
                          ...formData.stores[0].openingHours[
                            key as keyof Types.OpeningHoursType
                          ],
                        }}
                        onChangeActive={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => handleBusinessHourChange(event, "active")}
                        onChangeAbertura={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => handleBusinessHourChange(event, "from")}
                        onChangeFechamento={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => handleBusinessHourChange(event, "to")}
                      />
                    );
                  })}
              </div>
              <div className={styles.right_bottom_working_days}>
                {Object.keys(formData.stores[0].openingHours)
                  .slice(5, 7)
                  .map((key) => {
                    return (
                      <BusinessHour
                        key={key}
                        required={
                          formData.stores[0].openingHours[
                            key as keyof Types.OpeningHoursType
                          ].active
                        }
                        businessHoursInfo={{
                          name: key,
                          ...formData.stores[0].openingHours[
                            key as keyof Types.OpeningHoursType
                          ],
                        }}
                        onChangeActive={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => handleBusinessHourChange(event, "active")}
                        onChangeAbertura={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => handleBusinessHourChange(event, "from")}
                        onChangeFechamento={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => handleBusinessHourChange(event, "to")}
                      />
                    );
                  })}
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset className={styles.fieldset_service_info}>
          <h2>{t(`accountMenu.service`)}</h2>
          <h3>{t(`accountMenu.fillServiceInfo`)}</h3>
          <div className={styles.store_address_container}>
            <h4>{t(`accountMenu.storeAddress`)}</h4>
            <div className={styles.address_inputs_container}>
              <div className={styles.cep_and_state_container}>
                <div className={styles.address_input_container}>
                  <label className={styles.label_cep} htmlFor="input_cep">
                    {t(`accountMenu.cep`)}
                  </label>
                  <InputMask
                    mask={"99999-999"}
                    id="input_cep"
                    name="input_cep"
                    required
                    value={formData.stores[0].address.zipCode}
                    onBlur={(event) => handleCepBlur(event)}
                    onChange={(event) =>
                      setFormData((prev) => {
                        const updatedStores = [...prev.stores];

                        updatedStores[0] = {
                          ...updatedStores[0],
                          address: {
                            ...updatedStores[0].address,
                            zipCode: event.target.value.replace(/\D/g, ""),
                          },
                        };

                        return {
                          ...prev,
                          stores: updatedStores,
                        };
                      })
                    }
                  />
                </div>
                <div className={styles.address_input_container}>
                  <label htmlFor="selectState">{t(`accountMenu.state`)}</label>
                  <select
                    className={styles.select_state}
                    name="selectState"
                    id="selectState"
                    required
                    value={formData.stores[0].address.state}
                    onChange={(event) => {
                      setFormData((prev) => {
                        const updatedStores = [...prev.stores];
                        updatedStores[0] = {
                          ...updatedStores[0],
                          address: {
                            ...updatedStores[0].address,
                            state: event.target.value,
                          },
                        };
                        return {
                          ...prev,
                          stores: updatedStores,
                        };
                      });
                    }}
                  >
                    <option value={t(`accountMenu.uf`)} disabled>
                      {t(`accountMenu.uf`)}
                    </option>
                    {states.length > 0 &&
                      states
                        .sort((a, b) => (a.sigla < b.sigla ? -1 : 1))
                        .map((item) => {
                          return (
                            <option key={item.id} value={item.sigla}>
                              {item.sigla}
                            </option>
                          );
                        })}
                  </select>
                </div>
              </div>
              <div className={styles.address_input_container}>
                <label htmlFor="selectCity">{t(`accountMenu.city`)}</label>
                <select
                  name="selectCity"
                  id="selectCity"
                  className={styles.select_city}
                  required
                  disabled={formData.stores[0].address.state === "Uf"}
                  value={formData.stores[0].address.city}
                  onChange={(event) =>
                    setFormData((prev) => {
                      const updatedStores = [...prev.stores];
                      updatedStores[0] = {
                        ...updatedStores[0],
                        address: {
                          ...updatedStores[0].address,
                          city: event.target.value,
                        },
                      };
                      return {
                        ...prev,
                        stores: updatedStores,
                      };
                    })
                  }
                >
                  <option value={t(`accountMenu.select`)} disabled>
                    {t(`accountMenu.select`)}
                  </option>

                  {formData.stores[0].address.state &&
                    formData.stores[0].address.state !== "Uf" &&
                    cities.map((city) => (
                      <option key={city.id} value={city.nome}>
                        {city.nome}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div
              className={`${styles.input_address_container} ${styles.address_input_container}`}
            >
              <label htmlFor="addressInput">{t(`accountMenu.address`)}</label>
              <input
                required
                type="text"
                name="addressInput"
                id="addressInput"
                value={formData.stores[0].address.street}
                onChange={(event) =>
                  setFormData((prev) => {
                    const updatedStores = [...prev.stores];
                    updatedStores[0] = {
                      ...updatedStores[0],
                      address: {
                        ...updatedStores[0].address,
                        street: event.target.value,
                      },
                    };
                    return {
                      ...prev,
                      stores: updatedStores,
                    };
                  })
                }
              />
            </div>
            <div className={styles.address_inputs_container}>
              <div className={styles.address_input_container}>
                <label htmlFor="numberAddressInput">
                  {t(`accountMenu.number`)}
                </label>
                <input
                  required
                  type="text"
                  name="numberAddressInput"
                  id="numberAddressInput"
                  placeholder="Ex: 123 ou S/N"
                  value={formData.stores[0].address.number ?? ""}
                  onChange={(event) =>
                    setFormData((prev) => {
                      const updatedStores = [...prev.stores];
                      updatedStores[0] = {
                        ...updatedStores[0],
                        address: {
                          ...updatedStores[0].address,
                          number: event.target.value,
                        },
                      };
                      return {
                        ...prev,
                        stores: updatedStores,
                      };
                    })
                  }
                />
              </div>
              <div className={styles.address_input_container}>
                <label htmlFor="complementInput">
                  {t(`accountMenu.addressComplement`)}
                </label>
                <input
                  type="text"
                  name="complementInput"
                  id="complementInput"
                  value={formData.stores[0].address.complement}
                  onChange={(event) =>
                    setFormData((prev) => {
                      const updatedStores = [...prev.stores];
                      updatedStores[0] = {
                        ...updatedStores[0],
                        address: {
                          ...updatedStores[0].address,
                          complement: event.target.value,
                        },
                      };
                      return {
                        ...prev,
                        stores: updatedStores,
                      };
                    })
                  }
                />
              </div>
            </div>
          </div>
          <div className={styles.deliveries_and_withdrawals_container}>
            <div className={styles.deliveries_container}>
              <p>
                {t(`accountMenu.storeDo`)}{" "}
                <span>{t(`accountMenu.deliveries`)}</span>?
              </p>
              <div className={styles.do_delivery_container}>
                <input
                  type="radio"
                  name="radioDeliveries"
                  id="doDelivery"
                  checked={formData.stores[0].serviceDelivery}
                  onChange={() =>
                    setFormData((prev) => {
                      const updatedStores = [...prev.stores];
                      updatedStores[0] = {
                        ...updatedStores[0],
                        serviceDelivery: true,
                      };
                      return {
                        ...prev,
                        stores: updatedStores,
                      };
                    })
                  }
                />
                <label htmlFor="doDelivery">{t(`accountMenu.yes`)}</label>
              </div>
              <div className={styles.dont_do_delivery_container}>
                <input
                  type="radio"
                  name="radioDeliveries"
                  id="dontDoDelivery"
                  checked={!formData.stores[0].serviceDelivery}
                  onChange={() =>
                    setFormData((prev) => {
                      const updatedStores = [...prev.stores];
                      updatedStores[0] = {
                        ...updatedStores[0],
                        serviceDelivery: false,
                      };
                      return {
                        ...prev,
                        stores: updatedStores,
                      };
                    })
                  }
                />
                <label htmlFor="dontDoDelivery">{t(`accountMenu.no`)}</label>
              </div>
            </div>
            <div className={styles.withdrawals_container}>
              <p>
                {t(`accountMenu.storeAllow`)}{" "}
                <span>{t(`accountMenu.withdrawals`)}</span>?
              </p>
              <div className={styles.do_withdrawals_container}>
                <input
                  type="radio"
                  name="radioWithdrawals"
                  id="doWithdrawals"
                  checked={formData.stores[0].servicePickup}
                  onChange={() =>
                    setFormData((prev) => {
                      const updatedStores = [...prev.stores];
                      updatedStores[0] = {
                        ...updatedStores[0],
                        servicePickup: true,
                      };
                      return {
                        ...prev,
                        stores: updatedStores,
                      };
                    })
                  }
                />
                <label htmlFor="doWithdrawals">{t(`accountMenu.yes`)}</label>
              </div>
              <div className={styles.dont_do_withdrawals_container}>
                <input
                  type="radio"
                  name="radioWithdrawals"
                  id="dontDoWithdrawals"
                  checked={!formData.stores[0].servicePickup}
                  onChange={() =>
                    setFormData((prev) => {
                      const updatedStores = [...prev.stores];
                      updatedStores[0] = {
                        ...updatedStores[0],
                        servicePickup: false,
                      };
                      return {
                        ...prev,
                        stores: updatedStores,
                      };
                    })
                  }
                />
                <label htmlFor="dontDoWithdrawals">{t(`accountMenu.no`)}</label>
              </div>
            </div>
          </div>
          <div className={styles.kilometer_radius_container}>
            <label htmlFor="kilometerRadiusInput">
              {t(`accountMenu.withdrawalsRadius`)}
            </label>
            <input
              type="number"
              name="kilometerRadiusInput"
              id="kilometerRadiusInput"
              min={"-1"}
              value={formData.stores[0].serviceRadius}
              disabled={
                isAllBrazilDelivery ? true : !formData.stores[0].serviceDelivery
              }
              required
              onChange={(event) =>
                setFormData((prev) => {
                  const updatedStores = [...prev.stores];
                  updatedStores[0] = {
                    ...updatedStores[0],
                    serviceRadius: Number(event.target.value),
                  };
                  return {
                    ...prev,
                    stores: updatedStores,
                  };
                })
              }
            />
            <span title={t(`accountMenu.kilometers`)}>
              {t(`accountMenu.km`)}
            </span>
          </div>
          <div className={styles.brazilDeliveryCheckbox}>
            <input
              type="checkbox"
              onChange={() => {
                setIsAllBrazilDelivery(!isAllBrazilDelivery);
              }}
              checked={isAllBrazilDelivery}
            />
            <span>{t(`accountMenu.brazilDelivery`)}</span>
          </div>
        </fieldset>
        <button type="submit">{t(`accountMenu.save`)}</button>
      </form>

      <div className={styles.finish_store_form}>
        <h2>{t(`accountMenu.finishStoreTitle`)}</h2>
        <h3>{t(`accountMenu.finishStoreSubtitle`)}</h3>
        <button type="button" onClick={() => setShowFinishStoreModal(true)}>
          {t(`accountMenu.finishStore`)}
        </button>
        <ModalEncerrarLoja
          isOpen={showFinishStoreModal}
          passwords={passwords}
          setPasswords={setPasswords}
          handleCancelar={() => setShowFinishStoreModal(false)}
          handleExcluir={(password: string) =>
            handleFinishStoreSubmit(password)
          }
        />
      </div>

      <ModalAceitarTermos
        handleAceitar={handleFinishStore}
        handleCancelar={() => {
          setTermsModalOpen(false);
        }}
        isOpen={termsModalOpen}
      />
    </div>
  );
};

export default EntrepreneurAccountData;
